import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

/**
 * Custom hook for handling authentication-required redirects
 * This ensures users are redirected back to their intended destination after login
 */
export const useAuthRedirect = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  /**
   * Redirects to login page with the current URL as redirect parameter
   * @param currentPath - Optional current path, defaults to current location
   */
  const redirectToLogin = useCallback((currentPath?: string) => {
    const redirectUrl = currentPath || `${window.location.pathname}${window.location.search}`;
    navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
  }, [navigate]);

  /**
   * Checks if user is authenticated, redirects to login if not
   * @param currentPath - Optional current path to redirect back to
   * @returns boolean indicating if user is authenticated
   */
  const requireAuth = useCallback((currentPath?: string) => {
    if (!isAuthenticated) {
      redirectToLogin(currentPath);
      return false;
    }
    return true;
  }, [isAuthenticated, redirectToLogin]);

  /**
   * Creates a login URL with redirect parameter
   * @param currentPath - Path to redirect to after login
   * @returns Login URL with redirect parameter
   */
  const getLoginUrlWithRedirect = useCallback((currentPath?: string) => {
    const redirectUrl = currentPath || `${window.location.pathname}${window.location.search}`;
    return `/login?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  return {
    isAuthenticated,
    redirectToLogin,
    requireAuth,
    getLoginUrlWithRedirect
  };
};

export default useAuthRedirect;
